import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Plus, Trash2, Play, Edit, Settings, RotateCcw, UserX, ChevronDown, ChevronUp, Download, Award, Trophy } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import CircleManager from "@/components/CircleManager";
import BattlesListManager from "@/components/director/BattlesListManager";
import TournamentIOToolbar from "@/components/director/TournamentIOToolbar";

import { API } from '../../utils/apiBase';

export const TournamentTab = ({
  tournaments, tournament, setTournament, battles, athletes, judges, judgesCount, setJudgesCount,
  showCreateForm, setShowCreateForm, createTournament, deleteTournament,
  tournamentName, setTournamentName, tournamentType, setTournamentType,
  tournamentFormat, setTournamentFormat, numCircles3, setNumCircles3,
  numCircles4, setNumCircles4, selectedJudges, setSelectedJudges,
  loadBattles, loadOptionalBattle, optionalBattle, optionalAthlete1, setOptionalAthlete1,
  optionalAthlete2, setOptionalAthlete2, createOptionalBattle, deleteOptionalBattle,
  startBattle, resetBattle, skipBattle, setForfeitBattleId, setForfeitModalOpen, loadData,
  tournamentCategory, setTournamentCategory, getAvailableAthletes, getCategoryFilteredAthletes,
  getRoundLabel, updateBattleAthletes, openEditJudgesModal, loadCurrentBattle,
  currentBattle, currentCircle, startCircle,
  setSelectedTournamentId: setSelectedTournamentIdFromParent,
}) => {
  const { t } = useI18n();
  const [editingBattle, setEditingBattle] = useState(null);
  const [editBattleAthlete1, setEditBattleAthlete1] = useState("");
  const [editBattleAthlete2, setEditBattleAthlete2] = useState("");
  // We deliberately do NOT keep a local "selectedTournamentId" state anymore:
  // the parent <DirectorPage> owns the canonical `tournament` prop, and any
  // selection change is propagated back via setSelectedTournamentIdFromParent.
  // This prevents the parent's 20 s loadData() poll from accidentally resetting
  // the user's pick to the first tournament when they scroll inside e.g. TOP32.
  const selectedTournamentId = tournament?.id || null;
  const [collapsedBattles, setCollapsedBattles] = useState({});
  const [editingBattleAthletes, setEditingBattleAthletes] = useState({});

  return (
    <>
            {/* Public Bracket Display - HIDDEN FOR NOW */}
            {false && tournament && (
              <div className="mb-6 p-4 bg-blue-900/30 border border-blue-500/50 rounded-lg flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-blue-300 mb-1">{t('tournament.publicBracket')}</h3>
                  <p className="text-sm text-blue-200">Share this link to display the tournament bracket on a big screen</p>
                </div>
                <Button
                  onClick={() => {
                    const url = `${window.location.origin}/tournament/${tournament.id}/bracket`;
                    window.open(url, '_blank');
                  }}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Open Bracket Display
                </Button>
              </div>
            )}
            
            {/* Collapsible Tournament Creation Form */}
            {(tournaments.length === 0 || showCreateForm) && (
              <Card className="p-6 glass mb-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-3xl font-bold">{t('tournament.createTitle')}</h2>
                  {tournaments.length > 0 && (
                    <Button
                      variant="ghost"
                      onClick={() => setShowCreateForm(false)}
                      className="text-muted-foreground"
                    >
                      {t('common.close')}
                    </Button>
                  )}
                </div>
                <p className="mb-2 text-lg text-muted-foreground">{t('tournament.availableAthletes')}: <span className="font-bold text-foreground">{athletes.length}</span></p>
                
                <div className="space-y-6 mt-6">
                  <Input
                    placeholder={t('tournament.namePlaceholder')}
                    value={tournamentName}
                    onChange={(e) => setTournamentName(e.target.value)}
                    data-testid="tournament-name-input"
                    className="text-xl h-14"
                  />
                  
                  {/* Tournament Type Selection */}
                  <div>
                    <label className="block text-xl font-semibold mb-3 text-foreground">{t('tournament.typeLabel')}:</label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <button
                        onClick={() => setTournamentType("bracket")}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          tournamentType === "bracket"
                            ? 'border-purple-500 bg-purple-500/20 text-foreground'
                            : 'border-border bg-muted/50 text-muted-foreground hover:border-gray-500'
                        }`}
                        data-testid="type-bracket"
                      >
                        <div className="text-2xl font-bold">{t('tournament.bracket')}</div>
                        <div className="text-sm mt-1">{t('tournament.bracketDesc')}</div>
                      </button>
                      <button
                        onClick={() => setTournamentType("circle_qualification")}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          tournamentType === "circle_qualification"
                            ? 'border-orange-500 bg-orange-500/20 text-foreground'
                            : 'border-border bg-muted/50 text-muted-foreground hover:border-gray-500'
                        }`}
                        data-testid="type-circle"
                      >
                        <div className="text-2xl font-bold">{t('tournament.circleQualification')}</div>
                        <div className="text-sm mt-1">{t('tournament.circleDesc')}</div>
                      </button>
                      <button
                        onClick={() => setTournamentType("1vs1_battles")}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          tournamentType === "1vs1_battles"
                            ? 'border-blue-500 bg-blue-500/20 text-foreground'
                            : 'border-border bg-muted/50 text-muted-foreground hover:border-gray-500'
                        }`}
                        data-testid="type-1vs1-battles"
                      >
                        <div className="text-2xl font-bold">{t('tournament.battlesList')}</div>
                        <div className="text-sm mt-1">{t('tournament.battlesListDesc')}</div>
                      </button>
                    </div>
                  </div>
                  
                  {/* Bracket Format Selection (only for bracket type) */}
                  {tournamentType === "bracket" && (
                    <div>
                      <label className="block text-xl font-semibold mb-3 text-foreground">{t('tournament.formatLabel')}:</label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                          { value: "TOP32", label: "TOP 32", athletes: 32, battles: 16 },
                          { value: "TOP16", label: "TOP 16", athletes: 16, battles: 8 },
                          { value: "TOP8", label: "TOP 8", athletes: 8, battles: 4 },
                          { value: "TOP4", label: "TOP 4", athletes: 4, battles: 2 }
                        ].map(format => (
                          <button
                            key={format.value}
                            onClick={() => setTournamentFormat(format.value)}
                            className={`p-4 rounded-lg border-2 transition-all ${
                              tournamentFormat === format.value
                                ? 'border-purple-500 bg-purple-500/20 text-foreground'
                                : 'border-border bg-muted/50 text-muted-foreground hover:border-gray-500'
                            }`}
                            data-testid={`format-${format.value}`}
                          >
                            <div className="text-2xl font-bold">{format.label}</div>
                            <div className="text-sm mt-1">{t('tournament.athletesCount').replace('{count}', format.athletes)}</div>
                            <div className="text-xs text-muted-foreground">{t('tournament.battlesCount').replace('{count}', format.battles)}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Circle Configuration (only for circle type) */}
                  {tournamentType === "circle_qualification" && (
                    <div>
                      <label className="block text-xl font-semibold mb-3 text-foreground">{t('tournament.circleConfig')}:</label>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 rounded-lg border border-border bg-muted/50">
                          <label className="block text-lg mb-2 text-orange-300">{t('tournament.circlesOf3')}</label>
                          <div className="flex items-center gap-3">
                            <button onClick={() => setNumCircles3(Math.max(0, numCircles3 - 1))} className="w-10 h-10 rounded bg-gray-700 text-foreground text-xl font-bold hover:bg-gray-600">-</button>
                            <span className="text-3xl font-bold text-foreground min-w-[3rem] text-center" data-testid="num-circles-3">{numCircles3}</span>
                            <button onClick={() => setNumCircles3(numCircles3 + 1)} className="w-10 h-10 rounded bg-gray-700 text-foreground text-xl font-bold hover:bg-gray-600">+</button>
                          </div>
                          <div className="text-sm text-muted-foreground mt-2">{t('tournament.athletesNeeded').replace('{count}', numCircles3 * 3)}</div>
                        </div>
                        <div className="p-4 rounded-lg border border-border bg-muted/50">
                          <label className="block text-lg mb-2 text-orange-300">{t('tournament.circlesOf4')}</label>
                          <div className="flex items-center gap-3">
                            <button onClick={() => setNumCircles4(Math.max(0, numCircles4 - 1))} className="w-10 h-10 rounded bg-gray-700 text-foreground text-xl font-bold hover:bg-gray-600">-</button>
                            <span className="text-3xl font-bold text-foreground min-w-[3rem] text-center" data-testid="num-circles-4">{numCircles4}</span>
                            <button onClick={() => setNumCircles4(numCircles4 + 1)} className="w-10 h-10 rounded bg-gray-700 text-foreground text-xl font-bold hover:bg-gray-600">+</button>
                          </div>
                          <div className="text-sm text-muted-foreground mt-2">{t('tournament.athletesNeeded').replace('{count}', numCircles4 * 4)}</div>
                        </div>
                      </div>
                      <div className="mt-3 text-lg text-gray-300">
                        {t('tournament.totalCircles').replace('{circles}', numCircles3 + numCircles4).replace('{athletes}', numCircles3 * 3 + numCircles4 * 4)}
                      </div>
                    </div>
                  )}
                  
                  {/* Category Selection */}
                  <div>
                    <label className="block text-xl font-semibold mb-3 text-foreground">{t('tournament.category')}:</label>
                    <div className="grid grid-cols-4 gap-3">
                      {[
                        { value: "ALL", label: t('tournament.categoryAll'), icon: t('common.all') },
                        { value: "Male", label: t('tournament.categoryMale'), icon: "\u2642" },
                        { value: "Female", label: t('tournament.categoryFemale'), icon: "\u2640" },
                        { value: "NextGen", label: t('tournament.categoryNextGen'), icon: "NG" }
                      ].map(cat => (
                        <button
                          key={cat.value}
                          onClick={() => setTournamentCategory(cat.value)}
                          className={`p-3 rounded-lg border-2 transition-all text-center ${
                            tournamentCategory === cat.value
                              ? 'border-purple-500 bg-purple-500/20 text-foreground'
                              : 'border-border bg-muted/50 text-muted-foreground hover:border-gray-500'
                          }`}
                          data-testid={`category-${cat.value}`}
                        >
                          <div className="text-xl font-bold">{cat.label}</div>
                          <div className="text-sm mt-1">{cat.icon}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Judge Count Selection */}
                  <div>
                    <label className="block text-xl font-semibold mb-3 text-foreground">{t('tournament.judgesCount')}:</label>
                    <div className="flex gap-3">
                      {[3, 5].map(count => (
                        <button
                          key={count}
                          onClick={() => {
                            setJudgesCount(count);
                            setSelectedJudges([]);  // Reset selection when changing count
                          }}
                          className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                            judgesCount === count
                              ? 'border-green-500 bg-green-500/20 text-foreground'
                              : 'border-border bg-muted/50 text-muted-foreground hover:border-gray-500'
                          }`}
                          data-testid={`judges-${count}`}
                        >
                          <div className="text-3xl font-bold">{count} {t('tournament.judgesToAssign')}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Judge Selection */}
                  <div>
                    <label className="block text-xl font-semibold mb-3 text-foreground">
                      {t('tournament.selectJudges')} ({selectedJudges.length}/{judgesCount})
                    </label>
                    <div className="grid grid-cols-5 gap-3">
                      {judges.map(judge => (
                        <button
                          key={judge.judge_number}
                          onClick={() => {
                            if (selectedJudges.includes(judge.judge_number)) {
                              setSelectedJudges(selectedJudges.filter(j => j !== judge.judge_number));
                            } else if (selectedJudges.length < judgesCount) {
                              setSelectedJudges([...selectedJudges, judge.judge_number]);
                            }
                          }}
                          className={`p-4 rounded-lg border-2 transition-all ${
                            selectedJudges.includes(judge.judge_number)
                              ? 'border-blue-500 bg-blue-500/20 text-foreground'
                              : 'border-border bg-muted/50 text-muted-foreground hover:border-gray-500'
                          }`}
                        >
                          <div className="text-4xl font-bold mb-1">#{judge.judge_number}</div>
                          <div className="text-sm">{judge.first_name}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <Button
                    onClick={createTournament}
                    disabled={!tournamentName || selectedJudges.length !== judgesCount || (tournamentType === "bracket" && athletes.length < (tournamentFormat === "TOP32" ? 32 : tournamentFormat === "TOP16" ? 16 : tournamentFormat === "TOP8" ? 8 : 4)) || (tournamentType === "circle_qualification" && numCircles3 + numCircles4 === 0)}
                    className="w-full text-2xl h-16"
                    data-testid="create-tournament-btn"
                  >
                    <Trophy size={28} className="mr-2" /> 
                    {tournamentType === "circle_qualification"
                      ? t('tournament.createCircleBtn').replace('{count}', numCircles3 + numCircles4)
                      : tournamentType === "1vs1_battles"
                      ? t('tournament.battlesList')
                      : t('tournament.createTournamentFormat').replace('{format}', tournamentFormat)
                    }
                  </Button>
                </div>
              </Card>
            )}
            
            {/* + Button when tournaments exist and form is hidden */}
            {tournaments.length > 0 && !showCreateForm && (
              <div className="mb-6">
                <Button
                  onClick={() => setShowCreateForm(true)}
                  className="w-full text-2xl h-16 border-2 border-dashed border-border"
                  variant="outline"
                >
                  <Plus size={28} className="mr-2" /> {t('tournament.newTournament')}
                </Button>
              </div>
            )}
            
            {/* Horizontal Tabs for Tournaments */}
            {tournaments.length > 0 && (
              <div>
                <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                  {tournaments.map(tourney => (
                    <button
                      key={tourney.id}
                      onClick={() => {
                        setTournament(tourney);
                        setJudgesCount(tourney.judges_count || 5);
                        loadBattles(tourney.id);
                        loadOptionalBattle(tourney.id);
                        // Also sync the parent's selectedTournamentId so the
                        // background loadData() polling won't reset our pick.
                        if (setSelectedTournamentIdFromParent) {
                          setSelectedTournamentIdFromParent(tourney.id);
                        }
                      }}
                      className={`px-6 py-3 rounded-lg whitespace-nowrap transition-all text-xl font-semibold ${
                        selectedTournamentId === tourney.id
                          ? 'bg-primary text-foreground'
                          : 'bg-muted text-muted-foreground hover:bg-muted'
                      }`}
                    >
                      {tourney.name} ({tourney.type === 'circle_qualification' ? t('tournament.circles') : tourney.type === '1vs1_battles' ? t('tournament.battlesList') : tourney.format})
                    </button>
                  ))}
                </div>
                
                {/* Selected Tournament Display */}
                {tournament && selectedTournamentId === tournament.id && (
                  <div>
                    <Card className="p-6 glass mb-6">
                      <div className="flex justify-between items-center">
                        <div>
                          <h2 className="text-3xl font-bold mb-2">{tournament.name}</h2>
                          <p className="text-xl text-muted-foreground">
                            {tournament.type === 'circle_qualification'
                              ? `${t('tournament.circleQualification')} | ${tournament.num_circles_3 || 0}x3 + ${tournament.num_circles_4 || 0}x4 | ${t('tournament.judgesToAssign')}: ${tournament.judges_count}`
                              : tournament.type === '1vs1_battles'
                              ? `${t('tournament.battlesList')} | ${t('tournament.judgesToAssign')}: ${tournament.judges_count}`
                              : `${t('tournament.format')}: ${tournament.format} | ${t('tournament.judgesToAssign')}: ${tournament.judges_count}`
                            }
                            {tournament.category && tournament.category !== 'ALL' && ` | ${tournament.category}`}
                          </p>
                          {tournament.type !== 'circle_qualification' && tournament.type !== '1vs1_battles' && (
                            <p className="text-xl text-muted-foreground">{t('tournament.currentRound')}: {getRoundLabel(tournament.current_round)}</p>
                          )}
                          <p className="text-xl text-muted-foreground">{t('tournament.statusLabel')}: {tournament.status === 'active' ? t('tournament.statusActive') : tournament.status === 'completed' ? t('tournament.statusCompleted') : tournament.status === 'setup' ? t('tournament.statusSetup') : tournament.status}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            onClick={openEditJudgesModal}
                            className="text-foreground text-base sm:text-lg h-10 sm:h-12 px-3 sm:px-4"
                            disabled={currentBattle && currentBattle.battle?.tournament_id === tournament.id}
                            title={currentBattle && currentBattle.battle?.tournament_id === tournament.id ? t('tournament.cannotEditJudges') : t('tournament.editJudges')}
                          >
                            <Edit size={18} className="sm:mr-2" />
                            <span className="hidden sm:inline">{t('tournament.editJudges')}</span>
                          </Button>
                          <Button
                            variant="destructive"
                            onClick={deleteTournament}
                            data-testid="delete-tournament-btn"
                            className="text-base sm:text-lg h-10 sm:h-12 px-3 sm:px-4"
                          >
                            <Trash2 size={18} className="sm:mr-2" />
                            <span className="hidden sm:inline">{t('tournament.deleteTournament')}</span>
                          </Button>
                          {(tournament.type === 'bracket' || tournament.type === '1vs1_battles') && tournament.type === 'bracket' && (
                            <TournamentIOToolbar
                              templateUrl={`${API}/tournament/${tournament.id}/battles/template`}
                              importUrl={`${API}/tournament/${tournament.id}/battles/import`}
                              exportUrl={`${API}/tournament/${tournament.id}/battles/export`}
                              testIdPrefix="bracket-io"
                              onImported={() => loadData && loadData()}
                            />
                          )}
                          {tournament.type !== 'bracket' && tournament.type !== '1vs1_battles' && tournament.type !== 'circle_qualification' && (
                            <Button
                              variant="outline"
                              onClick={() => window.open(`${API}/tournament/${tournament.id}/battles/export`, '_blank')}
                              data-testid="export-battle-results-btn"
                              className="text-base sm:text-lg h-10 sm:h-12 px-3 sm:px-4"
                            >
                              <Download size={18} className="sm:mr-2" />
                              <span className="hidden sm:inline">{t('tournament.exportResults')}</span>
                            </Button>
                          )}
                        </div>
                      </div>
                    </Card>

                    {/* Circle Manager for circle_qualification tournaments */}
                    {tournament.type === 'circle_qualification' && (
                      <CircleManager 
                        tournament={tournament} 
                        athletes={athletes.filter(a => {
                          const cat = tournament.category || 'ALL';
                          if (cat === 'ALL') return true;
                          if (cat === 'Male') return a.gender === 'Male';
                          if (cat === 'Female') return a.gender === 'Female';
                          if (cat === 'NextGen') return a.nextgen === true;
                          return true;
                        })} 
                        judges={judges}
                        onReload={loadData}
                        startCircle={startCircle}
                      />
                    )}

                    {/* 1v1 Battles Manager for 1vs1_battles tournaments */}
                    {tournament.type === '1vs1_battles' && (
                      <BattlesListManager
                        tournament={tournament}
                        athletes={athletes}
                        onReload={loadData}
                        startBattle={startBattle}
                        resetBattle={resetBattle}
                        currentBattleId={currentBattle?.battle?.id}
                        currentBattleStatus={currentBattle?.battle?.status}
                      />
                    )}

                    {/* Battles Display - only for bracket tournaments */}
                    {tournament.type !== 'circle_qualification' && tournament.type !== '1vs1_battles' && (() => {
                      // Full round sequence for each bracket format. This is the
                      // ONLY place the UI decides which rounds to render for a
                      // given format — the backend already creates the matching
                      // battle documents up-front, so we must not skip any round
                      // here or entire rounds disappear from the manager view
                      // (bug: a TOP32 was showing TOP32 → QUARTER_FINALS with
                      // TOP16 skipped, even though the 8 TOP16 battles existed
                      // in Mongo).
                      const ROUND_SEQUENCES = {
                        TOP32: ['TOP32', 'TOP16', 'QUARTER_FINALS', 'SEMI_FINALS', 'THIRD_PLACE', 'FINAL'],
                        TOP16: ['TOP16', 'QUARTER_FINALS', 'SEMI_FINALS', 'THIRD_PLACE', 'FINAL'],
                        TOP8:  ['QUARTER_FINALS', 'SEMI_FINALS', 'THIRD_PLACE', 'FINAL'],
                        TOP4:  ['SEMI_FINALS', 'THIRD_PLACE', 'FINAL'],
                      };
                      const roundSequence = ROUND_SEQUENCES[tournament.format]
                        || ROUND_SEQUENCES.TOP16;
                      return (
                    <div className="space-y-6">
                      {roundSequence.map(round => {
                        const roundBattles = battles.filter(b => b && b.id && b.round === round);
                        if (roundBattles.length === 0) return null;
                        return (
                          <div key={round}>
                            <h3 className="text-3xl font-bold mb-4">{getRoundLabel(round)}</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {roundBattles.map(battle => {
                                const athlete1 = athletes.find(a => a && a.id === battle.athlete1_id);
                                const athlete2 = athletes.find(a => a && a.id === battle.athlete2_id);
                                return (
                                  <Card key={battle.id} className="p-5 glass" data-testid={`battle-card-${battle.id}`}>
                                    <div className="flex justify-between items-center mb-3">
                                      <span className="font-bold text-4xl text-foreground">Battle {battle.match_number}</span>
                                      <div className="flex gap-2 items-center">
                                        <span className="text-2xl px-3 py-1 rounded bg-purple-500/20 text-foreground font-semibold">{battle.status}</span>
                                        {battle.status === 'completed' && (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setCollapsedBattles(prev => ({...prev, [battle.id]: !prev[battle.id]}))}
                                            className="text-muted-foreground text-2xl"
                                          >
                                            {collapsedBattles[battle.id] ? '▼' : '▲'}
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Collapsed view for completed battles */}
                                    {battle.status === 'completed' && collapsedBattles[battle.id] ? (
                                      <div className="flex justify-between items-center py-2">
                                        <div className="text-xl text-green-400 font-bold flex items-center gap-2">
                                          <Trophy size={20} />
                                          {athletes.find(a => a && a.id === battle.winner_id)?.name || 'Unknown'}
                                        </div>
                                        <div className="text-lg text-muted-foreground font-semibold">
                                          {battle.athlete1_total_score} - {battle.athlete2_total_score}
                                        </div>
                                      </div>
                                    ) : (
                                      <>
                                        {/* Athlete Selection for TBD battles - ONLY for first round */}
                                        {(!battle.athlete1_id || !battle.athlete2_id) ? (
                                          (() => {
                                            // Check if this battle is in the first round (manually assignable)
                                            // First round varies by format:
                                            // TOP32 -> TOP32, TOP16 -> TOP16, TOP8 -> QUARTER_FINALS, TOP4 -> SEMI_FINALS
                                            const firstRoundMap = {
                                              "TOP32": "TOP32",
                                              "TOP16": "TOP16",
                                              "TOP8": "QUARTER_FINALS",
                                              "TOP4": "SEMI_FINALS"
                                            };
                                            const firstRoundName = firstRoundMap[tournament.format];
                                            const isFirstRound = battle.round === firstRoundName;
                                            
                                            if (!isFirstRound) {
                                              // Not first round - show waiting message
                                              return (
                                                <div className="text-center py-6 text-muted-foreground text-lg">
                                                  <div className="mb-2">⏳ Waiting for previous round results</div>
                                                  <div className="text-base">Athletes will be assigned automatically</div>
                                                </div>
                                              );
                                            }
                                            
                                            // First round - show assignment dropdowns
                                            return (
                                              <div className="space-y-4">
                                                <div className="grid grid-cols-2 gap-4">
                                                  <div>
                                                    <label className="block text-lg mb-2 text-foreground">{t('tournament.athlete1')}</label>
                                                    <select
                                                      value={editingBattleAthletes[battle.id]?.athlete1 || battle.athlete1_id || ""}
                                                      onChange={(e) => setEditingBattleAthletes(prev => ({
                                                        ...prev,
                                                        [battle.id]: { ...prev[battle.id], athlete1: e.target.value }
                                                      }))}
                                                      className="w-full p-2 bg-muted text-foreground rounded text-lg"
                                                    >
                                                      <option value="">{t('tournament.selectAthlete')}</option>
                                                      {getAvailableAthletes(
                                                        battle.id, 
                                                        editingBattleAthletes[battle.id]?.athlete2 || battle.athlete2_id
                                                      ).map(athlete => (
                                                        <option key={athlete.id} value={athlete.id}>{athlete.name}</option>
                                                      ))}
                                                    </select>
                                                  </div>
                                                  <div>
                                                    <label className="block text-lg mb-2 text-foreground">{t('tournament.athlete2')}</label>
                                                    <select
                                                      value={editingBattleAthletes[battle.id]?.athlete2 || battle.athlete2_id || ""}
                                                      onChange={(e) => setEditingBattleAthletes(prev => ({
                                                        ...prev,
                                                        [battle.id]: { ...prev[battle.id], athlete2: e.target.value }
                                                      }))}
                                                      className="w-full p-2 bg-muted text-foreground rounded text-lg"
                                                    >
                                                      <option value="">{t('tournament.selectAthlete')}</option>
                                                      {getAvailableAthletes(
                                                        battle.id, 
                                                        editingBattleAthletes[battle.id]?.athlete1 || battle.athlete1_id
                                                      ).map(athlete => (
                                                        <option key={athlete.id} value={athlete.id}>{athlete.name}</option>
                                                      ))}
                                                    </select>
                                                  </div>
                                                </div>
                                                <Button
                                                  onClick={() => updateBattleAthletes(
                                                    battle.id,
                                                    editingBattleAthletes[battle.id]?.athlete1 || battle.athlete1_id,
                                                    editingBattleAthletes[battle.id]?.athlete2 || battle.athlete2_id
                                                  )}
                                                  className="w-full text-xl h-12"
                                                  disabled={
                                                    !(editingBattleAthletes[battle.id]?.athlete1 || battle.athlete1_id) ||
                                                    !(editingBattleAthletes[battle.id]?.athlete2 || battle.athlete2_id)
                                                  }
                                                >
                                                  {t('tournament.assignAthletes')}
                                                </Button>
                                              </div>
                                            );
                                          })()
                                        ) : (
                                          <>
                                            <div className="flex justify-between items-center mb-2">
                                              <div className="flex-1 text-center">
                                                <div className="text-3xl font-bold text-foreground">
                                                  {battle.athlete1_id === 'BYE' ? 'BYE' : (athlete1?.name || 'TBD')}
                                                </div>
                                                {athlete1 && battle.athlete1_id !== 'BYE' && (
                                                  <>
                                                    <div className="text-base text-muted-foreground mt-1">{athlete1.country}</div>
                                                    {athlete1.world_ranking && (
                                                      <div className="text-lg text-yellow-400 font-semibold">#{athlete1.world_ranking}</div>
                                                    )}
                                                  </>
                                                )}
                                              </div>
                                              <div className="text-foreground text-3xl font-bold px-4">VS</div>
                                              <div className="flex-1 text-center">
                                                <div className="text-3xl font-bold text-foreground">
                                                  {battle.athlete2_id === 'BYE' ? 'BYE' : (athlete2?.name || 'TBD')}
                                                </div>
                                                {athlete2 && battle.athlete2_id !== 'BYE' && (
                                                  <>
                                                    <div className="text-base text-muted-foreground mt-1">{athlete2.country}</div>
                                                    {athlete2.world_ranking && (
                                                      <div className="text-lg text-yellow-400 font-semibold">#{athlete2.world_ranking}</div>
                                                    )}
                                                  </>
                                                )}
                                              </div>
                                            </div>
                                            <div className="text-xl text-foreground text-center mb-3">Votes: {battle.votes_count}/{tournament.judges_count || 5}</div>
                                            
                                            {/* Show edit interface if editing this battle */}
                                            {editingBattleAthletes[battle.id]?.isEditing ? (
                                              <div className="space-y-4 mb-4">
                                                <div className="grid grid-cols-2 gap-4">
                                                  <div>
                                                    <label className="block text-lg mb-2 text-foreground">{t('tournament.athlete1')}</label>
                                                    <select
                                                      value={editingBattleAthletes[battle.id]?.athlete1 || battle.athlete1_id || ""}
                                                      onChange={(e) => setEditingBattleAthletes(prev => ({
                                                        ...prev,
                                                        [battle.id]: { ...prev[battle.id], athlete1: e.target.value }
                                                      }))}
                                                      className="w-full p-2 bg-muted text-foreground rounded text-lg"
                                                    >
                                                      <option value="">{t('tournament.selectAthlete')}</option>
                                                      {getAvailableAthletes(
                                                        battle.id, 
                                                        editingBattleAthletes[battle.id]?.athlete2 || battle.athlete2_id
                                                      ).map(athlete => (
                                                        <option key={athlete.id} value={athlete.id}>{athlete.name}</option>
                                                      ))}
                                                    </select>
                                                  </div>
                                                  <div>
                                                    <label className="block text-lg mb-2 text-foreground">{t('tournament.athlete2')}</label>
                                                    <select
                                                      value={editingBattleAthletes[battle.id]?.athlete2 || battle.athlete2_id || ""}
                                                      onChange={(e) => setEditingBattleAthletes(prev => ({
                                                        ...prev,
                                                        [battle.id]: { ...prev[battle.id], athlete2: e.target.value }
                                                      }))}
                                                      className="w-full p-2 bg-muted text-foreground rounded text-lg"
                                                    >
                                                      <option value="">{t('tournament.selectAthlete')}</option>
                                                      {getAvailableAthletes(
                                                        battle.id, 
                                                        editingBattleAthletes[battle.id]?.athlete1 || battle.athlete1_id
                                                      ).map(athlete => (
                                                        <option key={athlete.id} value={athlete.id}>{athlete.name}</option>
                                                      ))}
                                                    </select>
                                                  </div>
                                                </div>
                                                <div className="flex gap-2">
                                                  <Button
                                                    onClick={() => {
                                                      updateBattleAthletes(
                                                        battle.id,
                                                        editingBattleAthletes[battle.id]?.athlete1 || battle.athlete1_id,
                                                        editingBattleAthletes[battle.id]?.athlete2 || battle.athlete2_id
                                                      );
                                                    }}
                                                    className="flex-1 text-xl h-12"
                                                    disabled={
                                                      !(editingBattleAthletes[battle.id]?.athlete1 || battle.athlete1_id) ||
                                                      !(editingBattleAthletes[battle.id]?.athlete2 || battle.athlete2_id)
                                                    }
                                                  >
                                                    Save Changes
                                                  </Button>
                                                  <Button
                                                    onClick={() => {
                                                      setEditingBattleAthletes(prev => {
                                                        const newState = {...prev};
                                                        delete newState[battle.id];
                                                        return newState;
                                                      });
                                                    }}
                                                    variant="outline"
                                                    className="flex-1 text-xl h-12"
                                                  >
                                                    Cancel
                                                  </Button>
                                                </div>
                                              </div>
                                            ) : (
                                              <div className="flex gap-2">
                                                {battle.status === 'pending' && (() => {
                                                  // If either side is a BYE, the primary action becomes
                                                  // "Skip the battle" (walkover) instead of "Start".
                                                  // The Edit button MUST stay reachable so the manager
                                                  // can always undo a wrong BYE assignment without
                                                  // having to reset the whole battle.
                                                  const hasBye =
                                                    battle.athlete1_id === 'BYE' || battle.athlete2_id === 'BYE';
                                                  const primary = hasBye ? (
                                                    <Button
                                                      onClick={() => skipBattle && skipBattle(battle.id)}
                                                      className="flex-1 text-xl h-14 bg-purple-600 hover:bg-purple-700"
                                                      data-testid={`skip-battle-${battle.id}`}
                                                    >
                                                      <Play size={22} className="mr-2" /> {t('battle.skipBye') || 'Skip the battle'}
                                                    </Button>
                                                  ) : (
                                                    <Button
                                                      onClick={() => startBattle(battle.id)}
                                                      className="flex-1 text-xl h-14"
                                                      data-testid={`start-battle-${battle.id}`}
                                                    >
                                                      <Play size={22} className="mr-2" /> Start
                                                    </Button>
                                                  );
                                                  return (
                                                    <>
                                                      {primary}
                                                      <Button
                                                        onClick={() => {
                                                          setEditingBattleAthletes(prev => ({
                                                            ...prev,
                                                            [battle.id]: {
                                                              isEditing: true,
                                                              athlete1: battle.athlete1_id,
                                                              athlete2: battle.athlete2_id
                                                            }
                                                          }));
                                                        }}
                                                        variant="outline"
                                                        className="text-xl h-14 px-4 border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
                                                        data-testid={`edit-battle-${battle.id}`}
                                                      >
                                                        <Edit size={22} className="mr-2" /> Edit
                                                      </Button>
                                                      {!hasBye && (
                                                        <Button
                                                          onClick={() => {
                                                            setForfeitBattleId(battle.id);
                                                            setForfeitModalOpen(true);
                                                          }}
                                                          variant="outline"
                                                          className="text-xl h-14 px-4 border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/20"
                                                          data-testid={`forfeit-battle-${battle.id}`}
                                                        >
                                                          Forfeit
                                                        </Button>
                                                      )}
                                                    </>
                                                  );
                                                })()}
                                                {battle.status !== 'pending' && (
                                                  <Button
                                                    onClick={() => resetBattle(battle.id)}
                                                    variant="outline"
                                                    className="flex-1 text-xl h-14 border-orange-500/50 text-orange-400 hover:bg-orange-500/20"
                                                    data-testid={`reset-battle-${battle.id}`}
                                                  >
                                                    <RotateCcw size={22} className="mr-2" /> Reset
                                                  </Button>
                                                )}
                                              </div>
                                            )}
                                            {battle.status === 'completed' && battle.winner_id && (
                                              <div className="mt-4 pt-4 border-t border-border">
                                                <div className="text-green-400 font-bold text-xl mb-2">
                                                  <Award size={22} className="inline mr-1" />
                                                  Winner: {battle.winner_id === 'BYE' ? 'BYE' : athletes.find(a => a && a.id === battle.winner_id)?.name}
                                                </div>
                                                <div className="text-xl text-muted-foreground">
                                                  Score: {battle.athlete1_total_score} - {battle.athlete2_total_score}
                                                </div>
                                              </div>
                                            )}
                                          </>
                                        )}
                                      </>
                                    )}
                                  </Card>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                      );
                    })()}

                    {/* Optional Battle Section - only for bracket tournaments */}
                    {tournament.type !== 'circle_qualification' && tournament.type !== '1vs1_battles' && (
                    <Card className="p-6 glass mt-6">
                      <h2 className="text-3xl font-bold mb-4">{t('battle.optional')}</h2>
                      <p className="text-muted-foreground mb-4">Create a standalone battle independent from the tournament</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-xl mb-2 text-foreground">{t('tournament.athlete1')}</label>
                          <select
                            value={optionalAthlete1}
                            onChange={(e) => setOptionalAthlete1(e.target.value)}
                            className="w-full p-3 bg-muted text-foreground rounded text-lg"
                          >
                            <option value="">Select athlete...</option>
                            {getCategoryFilteredAthletes().filter(a => a.id !== optionalAthlete2).map(athlete => (
                              <option key={athlete.id} value={athlete.id}>{athlete.name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xl mb-2 text-foreground">{t('tournament.athlete2')}</label>
                          <select
                            value={optionalAthlete2}
                            onChange={(e) => setOptionalAthlete2(e.target.value)}
                            className="w-full p-3 bg-muted text-foreground rounded text-lg"
                          >
                            <option value="">Select athlete...</option>
                            {getCategoryFilteredAthletes().filter(a => a.id !== optionalAthlete1).map(athlete => (
                              <option key={athlete.id} value={athlete.id}>{athlete.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      <Button
                        onClick={createOptionalBattle}
                        disabled={!optionalAthlete1 || !optionalAthlete2 || optionalAthlete1 === optionalAthlete2}
                        className="w-full text-xl h-14"
                      >
                        <Plus size={22} className="mr-2" /> {t('battle.createOptional')}
                      </Button>
                      
                      {optionalBattle && optionalBattle.status !== 'completed' && (
                        <Card className="p-4 bg-muted/50 mt-4">
                          <div className="flex justify-between items-center mb-3">
                            <span className="font-bold text-2xl text-foreground">{t('battle.optionalActive')}</span>
                            <div className="flex gap-2 items-center">
                              <span className="text-xl px-3 py-1 rounded bg-purple-500/20 text-foreground font-semibold">{optionalBattle.status}</span>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={deleteOptionalBattle}
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </div>
                          <div className="flex justify-between items-center mb-2">
                            <div className="text-2xl font-bold text-foreground">{athletes.find(a => a && a.id === optionalBattle.athlete1_id)?.name || 'TBD'}</div>
                            <div className="text-foreground text-2xl font-bold">VS</div>
                            <div className="text-2xl font-bold text-foreground">{athletes.find(a => a && a.id === optionalBattle.athlete2_id)?.name || 'TBD'}</div>
                          </div>
                          <div className="text-lg text-foreground text-center mb-3">{t('common.votes')}: {optionalBattle.votes_count}/{tournament.judges_count || 5}</div>
                          <div className="flex gap-2">
                            {optionalBattle.status === 'pending' && (
                              <Button
                                onClick={() => startBattle(optionalBattle.id)}
                                className="flex-1 text-xl h-14"
                              >
                                <Play size={20} className="mr-2" /> {t('common.start')}
                              </Button>
                            )}
                            {optionalBattle.status !== 'pending' && (
                              <Button
                                onClick={() => resetBattle(optionalBattle.id)}
                                variant="outline"
                                className="flex-1 text-xl h-14 border-orange-500/50 text-orange-400 hover:bg-orange-500/20"
                              >
                                <RotateCcw size={18} className="mr-2" /> {t('common.reset')}
                              </Button>
                            )}
                          </div>
                        </Card>
                      )}
                    </Card>
                    )}
                  </div>
                )}
              </div>
            )}
    </>
  );
};
